import { Checkbox, Col, message, Row, Table, Tag, Select } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  SwapRightOutlined,
} from "@ant-design/icons";
import { GreyTableContainer } from "../../../components/Table/GreyTable";
import { FlexCol, FlexRow } from "../../../components/Container/Container";
import Input from "../../../components/Input/Input";
import DatePicker from "../../../components/DatePicker/DatePicker";
import {
  getDetail,
  getInvoiceByCusCom,
  getInvoiceCountByCusCom,
  syncInvoice,
} from "../../../datasource/OrderCreditDatasource";
import {
  InvoiceOrderStatus,
  invoiceOrderStatusColor,
  invoiceOrderStatusLabel,
  OrderSubStatus,
  orderSubStatusColor,
  orderSubStatusLabel,
} from "../../../definitions/invoice";
import dayjs from "dayjs";
import { numberFormatter } from "../../../utility/Formatter";
import icon from "../../../resource/icon";
import { useRecoilState } from "recoil";
import { creditLimitDetailState } from "../../../store/CreditLimitDetail";
import axios, { AxiosRequestConfig } from "axios";
import color from "../../../resource/color";
import "./Invoice.css";

const REQUEST_DMY = "YYYY-MM-DD";
const datePattern = "DD/MM/YYYY";

export const InvoicesOrderCreditDetailTab: React.FC = () => {
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company } = userProfile;
  const navigate = useNavigate();

  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const id = pathSplit[3];

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>();
  const [count, setCount] = useState(0);
  const [updateDate, setUpdateDate] = useState();

  const [status, setStatus] = useState<OrderSubStatus[]>();
  const [search, setSearch] = useState<string>("");
  const [dateFilter, setDates] = useState<any>();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [syncing, setSyncing] = useState(false);
  const [recoilState, setRecoilState] = useRecoilState(creditLimitDetailState);

  useEffect(() => {
    setPage(1);
    fetchData();
  }, [status, search, dateFilter]);

  const getCountStatus = async () => {
    setLoading(true);
    const data = await getInvoiceCountByCusCom(id);
    setRecoilState({
      ...recoilState,
      countInvoiceStatus: {
        ALREADY_PAID: data.paid,
        WAITING_PAID: data.waiting,
      },
    });
  };

  const fetchData = async (p?: number) => {
    setLoading(true);
    const data = await getInvoiceByCusCom({
      customerCompanyId: id,
      paymentStatuses: status,
      search,
      startDate: dateFilter && dateFilter[0] ? dateFilter[0].format(REQUEST_DMY) : undefined,
      endDate: dateFilter && dateFilter[1] ? dateFilter[1].format(REQUEST_DMY) : undefined,
      page: p ? p : page,
      pageSize,
    });
    setData(data?.data || []);
    setCount(data?.count || 0);
    setUpdateDate(data?.updateDate);
    setLoading(false);
  };

  const syncData = async () => {
    setSyncing(true);
    let customerNo = recoilState.customerNo;
    if (!customerNo) {
      const detail = await getDetail(id);
      customerNo = detail.customerNo;
      setRecoilState({ ...recoilState, customerNo });
    }
    await syncInvoice({
      company,
      customerNo,
    })
      .then((res) => {
        if (res.success) {
          message.success("อัปเดตข้อมูลแล้ว");
          setPage(1);
          setSearch("");
          setDates([null, null]);
          getCountStatus();
          fetchData(1);
        } else {
          message.error("เกิดข้อผิดพลาด");
        }
      })
      .finally(() => {
        setSyncing(false);
      });
  };

  const columns = [
    {
      title: "รหัสเอกสาร",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      render: (invoiceNo: string, row: any) => {
        return (
          <FlexCol>
            <Text>{invoiceNo}</Text>
            <Text color='Text3' level={6}>
              {dayjs(row?.orderDate).format(datePattern)}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "กำหนดชำระ",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate: string, row: any) => {
        return (
          <FlexCol>
            <Text>{dayjs(dueDate).format(datePattern)}</Text>
          </FlexCol>
        );
      },
    },
    {
      title: "เกินกำหนด",
      dataIndex: "dueDate",
      key: "overdue",
      render: (dueDate: string, row: any) => {
        const overdue = dayjs(dueDate).diff(dayjs(), "days") * -1;
        return (
          <FlexCol>
            {row.paymentStatus === "ALREADY_PAID" ? (
              <Text color={"Text1"}>-</Text>
            ) : (
              <Text color={overdue >= 0 ? "error" : "Text1"}>
                {overdue >= 0 ? overdue : 0}&nbsp;วัน
              </Text>
            )}
          </FlexCol>
        );
      },
    },
    // {
    //   title: "วันที่ชำระเงิน",
    //   dataIndex: "postingDate",
    //   key: "postingDate",
    //   onlyPaid: true,
    //   render: (postingDate: string, row: any) => {
    //     const overdue = dayjs(postingDate).diff(dayjs(row.dueDate), "days");
    //     return (
    //       <FlexCol>
    //         <Text>{dayjs(postingDate).format(datePattern)}</Text>
    //         <Text color='Text3' level={6}>
    //           <Text color={overdue ? "error" : "Text1"}>
    //             {overdue ? `เกิน ${overdue} วัน` : ""}
    //           </Text>
    //         </Text>
    //       </FlexCol>
    //     );
    //   },
    // },
    {
      title: "ยอดรวม",
      dataIndex: "amt",
      key: "amt",
      render: (amt: number, row: any) => {
        return (
          <FlexCol>
            <Text>{numberFormatter(amt, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "PDC",
      dataIndex: "invoicePayments",
      key: "pdc",
      render: (invoicePayments: any, row: any) => {
        const sumPdc =
          (row.amtPDC || 0) +
          (invoicePayments?.reduce(
            (acc, inv) => (inv.paymentChannel?.includes("เช็ค") ? acc + inv?.rvAmt : acc),
            0,
          ) || 0);
        return (
          <FlexCol>
            <Text>{numberFormatter(sumPdc, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "เงินโอน",
      dataIndex: "invoicePayments",
      key: "sum",
      render: (invoicePayments: any, row: any) => {
        const sumPym = invoicePayments?.reduce(
          (acc, inv) => (inv.paymentChannel?.trim() === "เงินโอน" ? acc + inv?.rvAmt : acc),
          0,
        );
        return (
          <FlexCol>
            <Text>{numberFormatter(sumPym, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "ชำระอื่นๆ",
      dataIndex: "invoicePayments",
      key: "sum",
      render: (invoicePayments: any, row: any) => {
        const sumPym = invoicePayments?.reduce(
          (acc, inv) =>
            !inv.paymentChannel?.includes("เช็ค") && !inv.paymentChannel?.includes("เงินโอน")
              ? acc + inv?.rvAmt
              : acc,
          0,
        );
        return (
          <FlexCol>
            <Text>{numberFormatter(sumPym, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "ยอดคงเหลือ",
      dataIndex: "invoicePayments",
      key: "remaining",
      // hidden: status === OrderSubStatus.ALREADY_PAID,
      render: (invoicePayments: any, row: any) => {
        const sumPdc = invoicePayments?.reduce((acc, inv) => acc + inv?.rvAmt, 0);
        const remaining = row.amt - sumPdc;
        return (
          <FlexCol>
            <Text>{numberFormatter(remaining, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "สถานะ",
      dataIndex: "paymentStatus",
      key: "status",
      render: (status: string, row: any) => {
        const { isInvoice, invoiceStatus, invoicePayments, amt } = row;
        const map = {
          ALREADY_PAID: InvoiceOrderStatus.COMPLETE,
          PARTIAL_PAYMENT: InvoiceOrderStatus.NON_COMPLETE,
          WAITING_PAID: InvoiceOrderStatus.NON_PAYMENT,
        };
        const sumPym = invoicePayments?.reduce((acc, inv) => acc + inv?.rvAmt, 0);

        let mapStatus;
        if (isInvoice) {
          mapStatus = map[status];
        } else if (sumPym === 0) {
          mapStatus = InvoiceOrderStatus.NOT_USED;
        } else if (parseFloat(amt) > sumPym) {
          mapStatus = InvoiceOrderStatus.SOME_USED;
        } else {
          mapStatus = InvoiceOrderStatus.USED;
        }

        return (
          <Tag color={invoiceOrderStatusColor[mapStatus]} style={{ borderRadius: 12 }}>
            <Text color='white' fontWeight={600} fontSize={14}>
              {invoiceOrderStatusLabel[mapStatus]}
            </Text>
          </Tag>
        );
      },
    },
    {
      title: "",
      dataIndex: "invoiceNo",
      key: "download",
      width: "5%",
      render: (invNo: string) => {
        const onDownload = async () => {
          const token = localStorage.getItem("token") || "";
          const headers = {
            "Content-Type": "blob",
            Authorization: token ? `Bearer ` + JSON.parse(token) : "",
          };
          const config: AxiosRequestConfig = {
            method: "GET",
            url: `https://system.icpladda.com/api/invoice/download?invNo=${invNo}&company=${company}`,
            responseType: "arraybuffer",
            headers,
            params: { invNo, company },
          };
          const response = await axios(config);

          const file = new Blob([response.data], {
            // type: "application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            type: "application/pdf",
          });

          const url = window.URL.createObjectURL(file);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `invoice-${invNo}-${new Date().toISOString().split("T")[0]}.pdf`,
          );

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        };
        return (
          <>
            {!invNo.includes("JV") && (
              <div
                className='btn btn-icon btn-light btn-hover-primary btn-sm'
                onClick={onDownload}
                style={{ cursor: "pointer" }}
              >
                <span className='svg-icon svg-icon-primary svg-icon-2x'>
                  {<img src={icon.downloadFileIcon} style={{ width: 15, height: 16 }} />}
                </span>
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <Row justify='space-between'>
        <Col span={14}>
          <Text fontWeight={700}>
            รายการใบแจ้งหนี้และเอกสารอื่นๆ (Invoices & Supporting Documents)
          </Text>
          <br />
          <Text level={6}>
            อัปเดตล่าสุดเมื่อ {updateDate ? dayjs(updateDate).format("DD/MM/YYYY, HH:mm") : "-"}
          </Text>
        </Col>
        <Col span={4}>
          <Button
            title='อัปเดตข้อมูลใหม่'
            typeButton='border-primary'
            icon={<img src={icon.reload} style={{ width: 15, height: 17, marginBottom: 4 }} />}
            onClick={() => syncData()}
            loading={syncing}
          />
        </Col>
      </Row>
      <Row
        align='middle'
        style={{
          margin: "12px 0",
          padding: 12,
          backgroundColor: color.background1,
          borderRadius: 4,
        }}
      >
        <ExclamationCircleOutlined style={{ marginBottom: 4, color: color.primary }} />
        &nbsp;
        <Text>
          เพื่อให้ข้อมูลรายการถูกต้องสมบูรณ์ กรุณาตรวจสอบหรืออัปเดตในระบบภายใน 7 วัน เช่น จำนวนเงิน,
          การชำระเงิน, สถานะต่างๆ เป็นต้น
        </Text>
      </Row>
      <Row align='bottom' justify='space-between' gutter={12} style={{ margin: "16px 0px" }}>
        <Col span={6}>
          <Input
            placeholder='ค้นหารหัสเอกสาร (Invoice, CN, JV)'
            suffix={<SearchOutlined />}
            onPressEnter={(e) => {
              const value = (e.target as HTMLTextAreaElement).value;
              setSearch(value);
            }}
          />
        </Col>
        <Col span={8}></Col>
        <Col span={4}>
          <DatePicker
            enablePast
            placeholder='วันที่เริ่มต้น → วันที่สิ้นสุด'
            onChange={(dateFilter: any) => {
              setDates(dateFilter);
            }}
            value={dateFilter}
            disabledDate={(current) =>
              current && current.isBefore(dayjs().subtract(3, "year").startOf("year"))
            }
          />
        </Col>
        <Col span={6}>
          <Select
            className='select-over-credit'
            allowClear
            placeholder={
              <div style={{ paddingTop: 6 }}>
                <Text color='Text3' level={6} fontFamily='Sarabun'>
                  สถานะทั้งหมด
                </Text>
              </div>
            }
            mode='multiple'
            maxTagCount='responsive'
            options={Object.entries(invoiceOrderStatusLabel)
              .filter(
                ([key]) => key !== InvoiceOrderStatus.ALL && key !== InvoiceOrderStatus.CANCELED,
              )
              .map(([key, label]) => ({
                key,
                label,
                value: key,
              }))}
            onChange={(val) => setStatus(val)}
            style={{ width: "100%", height: "40px" }}
            value={status}
            menuItemSelectedIcon={null}
            optionRender={(option: any) => {
              return (
                <Row justify={"space-between"}>
                  <Col>
                    <Checkbox checked={status?.includes(option.value)}>{option.label}</Checkbox>
                  </Col>
                </Row>
              );
            }}
          />
        </Col>
      </Row>
      <Row justify='end'>
        <Col span={6}>
          <FlexRow justify='end'>
            <Text>ทั้งหมด {count} รายการ</Text>
          </FlexRow>
        </Col>
      </Row>
      <br />
      <GreyTableContainer>
        <Table
          className='rounded-lg'
          columns={columns}
          dataSource={data || []}
          pagination={{
            pageSize: 10,
            total: count,
            showSizeChanger: false,
            onChange: (page) => {
              setPage(page);
              fetchData(page);
            },
            position: ["bottomCenter"],
          }}
          size='large'
          tableLayout='fixed'
          loading={loading}
        />
      </GreyTableContainer>
    </>
  );
};
